/**
 * EVC Borrow Dialog Content
 *
 * Uses standard wagmi hooks instead of wagmi-evc hooks to avoid
 * React context mismatches from multiple wagmi instances.
 */

'use client'

import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { BorrowCapDetail } from '@/ui/molecules/borrow-cap-detail/BorrowCapDetail'
import { SupplyCapDetail } from '@/ui/molecules/supply-cap-detail/SupplyCapDetail'
import { useCallback, useEffect, useMemo } from 'react'
import { type Address, parseUnits, zeroAddress } from 'viem'
import { useAccount, useChainId, useConfig, useReadContract, useWriteContract } from 'wagmi'

import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { isTimeoutError, useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { useTrackedLendBorrowAction } from '@/features/analytics'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { isUserRejection } from '@/lib/errors'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import {
  type BorrowTransactionItem,
  type DepositTransactionItem,
  type DisableControllerTransactionItem,
  type EnableCollateralTransactionItem,
  type EnableControllerTransactionItem,
  type TransactionItems,
  TransactionLabel,
  type WrapTransactionItem,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { BorrowSuccessView } from './components/BorrowSuccessView'

// =============================================================================
// Types
// =============================================================================

interface EvcBorrowDialogContentProps {
  closeDialog: () => void
  collateralVault: Address
  collateralSymbol: string
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  liabilityVault: Address
  liabilitySymbol: string
  borrowAmount: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  subAccountId?: number
  wrapCollateralFromNative?: boolean
  unwrapBorrowToNative?: boolean
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Whether we're modifying an existing position (affects dialog title) */
  isModifyingExistingPosition?: boolean
  /** List of collateral vaults already enabled for this position (to skip redundant enableCollateral calls) */
  enabledCollaterals?: Address[]
}

// =============================================================================
// Transaction Builder (wagmi-evc TransactionItems)
// =============================================================================

function buildBorrowTransactions({
  collateralVault,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralSymbol,
  liabilityVault,
  liabilityDecimals,
  liabilityAsset,
  liabilitySymbol,
  address,
  subAccountId = 0,
  wrapCollateralFromNative = false,
  borrowAmountBigInt,
  existingController,
  skipEnableCollateral = false,
}: {
  collateralVault: Address
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralSymbol: string
  liabilityVault: Address
  liabilityDecimals: number
  liabilityAsset: Address
  liabilitySymbol: string
  address: Address
  subAccountId?: number
  wrapCollateralFromNative?: boolean
  borrowAmountBigInt: bigint
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Skip enableCollateral call (collateral already enabled for existing positions with debt) */
  skipEnableCollateral?: boolean
}): TransactionItems {
  if (!address || address === zeroAddress) return []

  const position = getSubAccountAddress(address, subAccountId)
  const collateralAmountBigInt = parseUnits(collateralAmount || '0', collateralDecimals)

  if (collateralAmountBigInt === 0n && borrowAmountBigInt === 0n) return []

  const transactions: TransactionItems = []

  const collateralAssetInfo = {
    address: collateralAsset,
    name: collateralSymbol,
    symbol: collateralSymbol,
    decimals: collateralDecimals,
  }

  const liabilityAssetInfo = {
    address: liabilityAsset,
    name: liabilitySymbol,
    symbol: liabilitySymbol,
    decimals: liabilityDecimals,
  }

  // 1. If wrapping native, wrap first
  if (wrapCollateralFromNative && collateralAmountBigInt > 0n) {
    const wrapTransaction: WrapTransactionItem = {
      label: TransactionLabel.WRAP,
      vault: collateralVault,
      asset: collateralAssetInfo,
      from: address,
      to: position,
      value: collateralAmountBigInt.toString(),
      totalAssets: '0',
      totalShares: '0',
    }
    transactions.push(wrapTransaction)
  }

  // 2. Enable collateral vault
  // Skip if collateral is already enabled (existing positions with debt would fail with E_OutstandingDebt)
  if (collateralAmountBigInt > 0n && !skipEnableCollateral) {
    const enableCollateral: EnableCollateralTransactionItem = {
      label: TransactionLabel.ENABLE_COLLATERAL,
      vault: collateralVault,
      asset: collateralAssetInfo,
      from: position,
      to: position,
    }
    transactions.push(enableCollateral)
  }

  // 3. Deposit collateral (only if NOT wrapping)
  // For simple borrow: Permit2 spender is the vault, vault calls transferFrom via Permit2
  if (collateralAmountBigInt > 0n && !wrapCollateralFromNative) {
    const depositTransaction: DepositTransactionItem = {
      label: TransactionLabel.DEPOSIT,
      vault: collateralVault,
      asset: collateralAssetInfo,
      from: address,
      to: position,
      value: collateralAmountBigInt.toString(),
    }
    transactions.push(depositTransaction)
  }

  // Only switch controller if existingController differs from liabilityVault.
  // SECURITY: disableController reverts with E_OutstandingDebt if sub-account has debt
  const needsControllerSwitch =
    borrowAmountBigInt > 0n && existingController && existingController.toLowerCase() !== liabilityVault.toLowerCase()

  if (needsControllerSwitch) {
    const disableController: DisableControllerTransactionItem = {
      label: TransactionLabel.DISABLE_CONTROLLER,
      vault: existingController,
      asset: liabilityAssetInfo,
      from: position,
      to: position,
    }
    transactions.push(disableController)
  }

  if (borrowAmountBigInt > 0n) {
    const enableController: EnableControllerTransactionItem = {
      label: TransactionLabel.ENABLE_CONTROLLER,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: position,
    }
    transactions.push(enableController)
  }

  // 6. Borrow
  if (borrowAmountBigInt > 0n) {
    const borrowTransaction: BorrowTransactionItem = {
      label: TransactionLabel.BORROW,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: address, // Borrowed funds go to user's wallet
      value: borrowAmountBigInt.toString(),
    }
    transactions.push(borrowTransaction)
  }

  return transactions
}

// =============================================================================
// Inner Component (uses wagmi hooks)
// =============================================================================

function EvcBorrowDialogContentInner({
  closeDialog,
  collateralVault,
  collateralSymbol,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  liabilityVault,
  liabilitySymbol,
  borrowAmount,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  subAccountId = 0,
  wrapCollateralFromNative,
  unwrapBorrowToNative,
  existingController,
  isModifyingExistingPosition = false,
  enabledCollaterals = [],
}: EvcBorrowDialogContentProps) {
  const { address } = useAccount()
  const config = useConfig()
  const chainId = useChainId()
  const addSubaccount = useSubaccountStore((state) => state.addSubaccount)

  const collateralAmountBigInt = useMemo(
    () => parseUnits(collateralAmount || '0', collateralDecimals),
    [collateralAmount, collateralDecimals],
  )
  const borrowAmountBigInt = useMemo(
    () => parseUnits(borrowAmount || '0', liabilityDecimals),
    [borrowAmount, liabilityDecimals],
  )

  const position = useMemo(
    () => (address ? getSubAccountAddress(address, subAccountId) : undefined),
    [address, subAccountId],
  )

  // Guard: detect outstanding debt on existing controller that would block controller switching.
  // disableController reverts with E_OutstandingDebt if the sub-account has unpaid debt.
  const needsControllerSwitch =
    borrowAmountBigInt > 0n && !!existingController && existingController.toLowerCase() !== liabilityVault.toLowerCase()

  const { data: existingDebt } = useReadContract({
    address: existingController,
    abi: eVaultImplementationAbi,
    functionName: 'debtOf',
    args: position ? [position] : undefined,
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: needsControllerSwitch && !!position,
    },
  })

  const controllerSwitchBlocked = needsControllerSwitch && existingDebt !== undefined && existingDebt > 0n

  // Build the transaction items using wagmi-evc types
  const transactions = useMemo(() => {
    if (!address || address === zeroAddress) return []
    return buildBorrowTransactions({
      collateralVault,
      collateralAmount,
      collateralDecimals,
      collateralAsset,
      collateralSymbol,
      liabilityVault,
      liabilityDecimals,
      liabilityAsset,
      liabilitySymbol,
      address,
      subAccountId,
      wrapCollateralFromNative,
      borrowAmountBigInt,
      existingController,
      // Skip enableCollateral if this specific collateral vault is already enabled
      // Calling enableCollateral on a position with debt throws E_OutstandingDebt
      // Note: only skip for THIS collateral vault, not all collaterals
      skipEnableCollateral: enabledCollaterals.some((c) => c.toLowerCase() === collateralVault.toLowerCase()),
    })
  }, [
    address,
    collateralVault,
    collateralAmount,
    collateralDecimals,
    collateralAsset,
    collateralSymbol,
    liabilityVault,
    liabilityDecimals,
    liabilityAsset,
    liabilitySymbol,
    subAccountId,
    wrapCollateralFromNative,
    borrowAmountBigInt,
    existingController,
    enabledCollaterals,
  ])

  // Permit2 preflight for ERC20 collateral deposits (native collateral doesn't need it)
  // For simple borrow: spender is the vault (vault calls transferFrom via Permit2)
  const permit2 = usePermit2Preflight({
    token: wrapCollateralFromNative
      ? undefined
      : new Token({
          symbol: TokenSymbol(collateralSymbol),
          name: collateralSymbol,
          decimals: collateralDecimals,
          address: CheckedAddress(collateralAsset),
          unitPriceUsd: '0',
          ...(collateralLogoURI && { logoURI: collateralLogoURI }),
        }),
    amount: wrapCollateralFromNative ? undefined : collateralAmountBigInt,
    spender: wrapCollateralFromNative ? undefined : collateralVault,
    enabled: !!address && !wrapCollateralFromNative && collateralAmountBigInt > 0n,
  })

  // Use wagmi-evc's simulation hook for proper state overrides
  // Simulate WITHOUT permitTx - state overrides mock the allowance
  // The actual execution will prepend permitTx (see handleExecute)
  // Pass config explicitly to avoid wagmi context mismatch in production builds
  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions, // base transactions (without permitTx)
    query: {
      enabled: !!address && transactions.length > 0 && !controllerSwitchBlocked,
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })
  const { markSubmitted } = useTrackedLendBorrowAction({
    actionType: 'borrow',
    token: liabilitySymbol,
    market: liabilityVault,
    marketType: 'eborrow',
    amount: borrowAmount,
    txHash,
    isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Skip Permit2 when:
  // 1. Using native collateral (wrapCollateralFromNative)
  // 2. No collateral being added (borrow-only operation)
  const skipPermit2 = wrapCollateralFromNative || collateralAmountBigInt === 0n

  // Execute the batch - prepend permitTx for ERC20 collateral
  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) {
      console.error('[EvcBorrowDialogContent] Simulation not ready')
      return
    }

    try {
      const executionRequest = skipPermit2
        ? simulationData.request
        : buildPermit2BatchRequest({
            simulationData,
            permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
            chainId,
          })

      markSubmitted()
      writeContract(executionRequest as any)
    } catch (err) {
      console.error('[EvcBorrowDialogContent] Execute error:', err)
    }
  }, [simulationData, writeContract, skipPermit2, permit2.permitTx, permit2.status, chainId, markSubmitted])

  // ========== ACTION TIMEOUT TRACKING ==========
  // Track timeout for borrow execution to provide feedback when operations hang
  const borrowTimeout = useActionTimeout({
    timeoutMs: 15000, // 15 seconds for borrow operations
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error,
  })

  // Wrapped execute handler that tracks timeout
  const handleExecuteWithTimeout = useCallback(() => {
    borrowTimeout.handleActionClick(handleExecute)
  }, [borrowTimeout, handleExecute])

  const { priceUSD: collateralPriceUSD } = useAssetPrice({
    assetAddress: collateralAsset,
    vaultAddress: collateralVault,
  })
  const { priceUSD: liabilityPriceUSD } = useAssetPrice({
    assetAddress: liabilityAsset,
    vaultAddress: liabilityVault,
  })
  const collateralValueUSD = Number(collateralAmount || 0) * collateralPriceUSD
  const borrowValueUSD = Number(borrowAmount || 0) * liabilityPriceUSD

  // Determine action handler states for the grid
  const collateralToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(collateralSymbol),
        name: collateralSymbol,
        decimals: collateralDecimals,
        address: CheckedAddress(collateralAsset),
        unitPriceUsd: '0',
        ...(collateralLogoURI && { logoURI: collateralLogoURI }),
      }),
    [collateralSymbol, collateralDecimals, collateralAsset, collateralLogoURI],
  )

  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (skipPermit2) return 'success' // Skip Permit2 for native deposits or borrow-only
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving' || permit2.isApproving) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, skipPermit2])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    if (skipPermit2) return 'success' // Skip Permit2 for native deposits or borrow-only
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval' || permit2.status === 'approving' || permit2.isApproving) return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing' || permit2.isSigning) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, permit2.isSigning, skipPermit2])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    // Active transaction states take priority - never allow retry while tx is pending
    if (isWriting || isWaiting) return 'loading'
    if (isConfirmed) return 'success'

    const hasAmount = collateralAmountBigInt > 0n || borrowAmountBigInt > 0n
    // Only disable for non-user-rejection errors (allow retry after wallet rejection)
    const hasBlockingError =
      (simulationError && !isUserRejection(simulationError)) ||
      (writeError && !isUserRejection(writeError)) ||
      (waitError && !isUserRejection(waitError))

    // Check prerequisites - these must be met even after timeout
    // (clicking retry with missing prerequisites would be a no-op)
    if (controllerSwitchBlocked) return 'disabled'
    if (hasBlockingError) return 'disabled'
    if (!hasAmount) return 'disabled'

    if (skipPermit2) {
      // Native collateral or borrow-only: need simulation data to execute
      if (!simulationData) {
        // If timed out but still simulating, show loading (can't retry without data)
        // If timed out and not simulating, something is wrong - stay disabled
        return isSimulating ? 'loading' : 'disabled'
      }
      // Have simulation data - allow retry if timed out, or ready if normal flow
      return 'ready'
    }

    // ERC20 collateral: need permit2 flow + simulation data
    if (permit2.status !== 'ready') return 'disabled'
    if (!simulationData) {
      return isSimulating ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [
    permit2.status,
    simulationData,
    isSimulating,
    isWriting,
    isWaiting,
    isConfirmed,
    simulationError,
    writeError,
    waitError,
    skipPermit2,
    collateralAmountBigInt,
    borrowAmountBigInt,
    controllerSwitchBlocked,
  ])

  // Only show errors that aren't user rejections
  const displayError = useMemo(() => {
    // Timeout error takes priority for better UX feedback
    if (borrowTimeout.isTimedOut) {
      return borrowTimeout.displayError
    }
    const errors = [permit2.error, simulationError, writeError, waitError].filter(Boolean)
    return errors.find((err) => !isUserRejection(err as any)) || null
  }, [permit2.error, simulationError, writeError, waitError, borrowTimeout.isTimedOut, borrowTimeout.displayError])

  // Optimistic update: mark this subaccount as active when transaction confirms
  useEffect(() => {
    if (isConfirmed) {
      addSubaccount(subAccountId)
    }
  }, [isConfirmed, subAccountId, addSubaccount])

  // Handle success
  if (isConfirmed) {
    // Create tokens for success view
    const borrowToken = new Token({
      symbol: TokenSymbol(liabilitySymbol),
      name: liabilitySymbol,
      decimals: liabilityDecimals,
      address: CheckedAddress(liabilityAsset),
      unitPriceUsd: liabilityPriceUSD > 0 ? String(liabilityPriceUSD) : '0',
      ...(liabilityLogoURI && { logoURI: liabilityLogoURI }),
    })

    const collateralToken =
      collateralAmount && Number(collateralAmount) > 0
        ? new Token({
            symbol: TokenSymbol(collateralSymbol),
            name: collateralSymbol,
            decimals: collateralDecimals,
            address: CheckedAddress(collateralAsset),
            unitPriceUsd: collateralPriceUSD > 0 ? String(collateralPriceUSD) : '0',
            ...(collateralLogoURI && { logoURI: collateralLogoURI }),
          })
        : undefined

    return (
      <BorrowSuccessView
        collateralToken={collateralToken}
        collateralAmount={collateralToken ? NormalizedUnitNumber(collateralAmount) : undefined}
        borrowToken={borrowToken}
        borrowAmount={NormalizedUnitNumber(borrowAmount)}
        onProceed={closeDialog}
        proceedText="Done"
      />
    )
  }

  const collateralImage = getTokenImage(TokenSymbol(collateralSymbol))
  const liabilityImage = getTokenImage(TokenSymbol(liabilitySymbol))
  const hasCollateral = collateralAmount && Number(collateralAmount) > 0
  const hasBorrow = borrowAmount && Number(borrowAmount) > 0

  // Dynamic dialog title based on action
  const dialogTitle =
    hasBorrow && !hasCollateral
      ? 'Borrow More'
      : hasCollateral && !hasBorrow
        ? 'Add Collateral'
        : isModifyingExistingPosition
          ? 'Add to Position'
          : 'Open Position'

  return (
    <MultiPanelDialog>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogPanel>
        {/* Position Summary */}
        <div className="flex flex-col gap-4">
          {/* Collateral Deposit */}
          {hasCollateral && (
            <div className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-3">
                {collateralImage && (
                  <TokenImage
                    src={collateralImage}
                    alt={collateralSymbol}
                    width={32}
                    height={32}
                    className="rounded-full"
                    fallbackSrc={collateralLogoURI}
                    tokenAddress={collateralAsset}
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">
                    {wrapCollateralFromNative ? 'Deposit (Native)' : 'Deposit Collateral'}
                  </span>
                  <span className="font-medium text-sm text-white">{collateralSymbol}</span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-semibold text-lg text-white">
                  {Number(collateralAmount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </span>
                {collateralValueUSD > 0 && (
                  <span className="text-muted-foreground text-xs">
                    $
                    {collateralValueUSD.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Borrow */}
          {hasBorrow && (
            <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3">
                {liabilityImage && (
                  <TokenImage
                    src={liabilityImage}
                    alt={liabilitySymbol}
                    width={32}
                    height={32}
                    className="rounded-full"
                    fallbackSrc={liabilityLogoURI}
                    tokenAddress={liabilityAsset}
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">
                    {unwrapBorrowToNative ? 'Borrow (Native)' : 'Borrow'}
                  </span>
                  <span className="font-medium text-sm text-white">{liabilitySymbol}</span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-semibold text-lg text-primary">
                  {Number(borrowAmount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </span>
                {borrowValueUSD > 0 && (
                  <span className="text-muted-foreground text-xs">
                    ${borrowValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          {hasCollateral && (
            <TransactionOverviewDetailsItem label="Collateral deposit">
              {Number(collateralAmount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {collateralSymbol}
            </TransactionOverviewDetailsItem>
          )}
          {hasBorrow && (
            <TransactionOverviewDetailsItem label="Borrow amount">
              {Number(borrowAmount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {liabilitySymbol}
            </TransactionOverviewDetailsItem>
          )}
          {hasBorrow && (
            <BorrowCapDetail vaultAddress={liabilityVault} decimals={liabilityDecimals} symbol={liabilitySymbol} />
          )}
          {hasCollateral && (
            <SupplyCapDetail vaultAddress={collateralVault} decimals={collateralDecimals} symbol={collateralSymbol} />
          )}
        </div>

        {/* Simulation Status */}
        {isSimulating && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Simulating transaction...
          </div>
        )}

        {/* Timeout error display - shows when operation takes too long */}
        <TimeoutErrorDisplay
          isTimedOut={borrowTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        {/* Controller switch blocked - outstanding debt prevents disableController */}
        {controllerSwitchBlocked && (
          <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
            <p className="text-red-400 text-sm">
              Cannot switch controller: your position has outstanding debt on the current liability vault. Repay your
              existing debt before borrowing from a different vault.
            </p>
          </div>
        )}

        {/* Error Display */}
        {displayError && !isTimeoutError(displayError) && (
          <TransactionErrorPanel
            error={displayError}
            showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
          />
        )}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid (matching deposit/repay dialog) */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* Step 1: Approve Permit2 */}
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
            <ActionRow.Title icon={<TokenIcon token={collateralToken} className="h-6" />} actionStatus={approveStatus}>
              Approve Permit2
            </ActionRow.Title>
            <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
              Approve
            </ActionRow.Action>
          </ActionRow>

          {/* Step 2: Sign Permit */}
          <ActionRow index={2}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
            <ActionRow.Title icon={<TokenIcon token={collateralToken} className="h-6" />} actionStatus={signStatus}>
              Sign Permit
            </ActionRow.Title>
            <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
            <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
              Sign
            </ActionRow.Action>
          </ActionRow>

          {/* Step 3: Execute */}
          <ActionRow index={3}>
            <ActionRow.Icon path={assets.actions.borrow} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={collateralToken} className="h-6" />} actionStatus={executeStatus}>
              {dialogTitle}
            </ActionRow.Title>
            <ActionRow.Description
              successMessage={
                hasBorrow && !hasCollateral ? 'Borrowed!' : hasCollateral && !hasBorrow ? 'Added!' : 'Position opened!'
              }
              actionStatus={executeStatus}
              variant="compact"
            />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
              {borrowTimeout.isTimedOut ? 'Retry' : isSimulating ? 'Preparing...' : dialogTitle}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}

// =============================================================================
// Exported Component
// =============================================================================

export function EvcBorrowDialogContent(props: EvcBorrowDialogContentProps) {
  return <EvcBorrowDialogContentInner {...props} />
}
